import { Invoice } from '../types';
import { calculateIssuedInvoiceTotals } from './fiscalUtils';

export const resolveInvoiceNumber = (
  invoiceData: { displayNumber?: string; number?: number },
  company?: { invoicePrefix?: string; currentYear?: number }
) => {
  if (invoiceData.displayNumber) return invoiceData.displayNumber;
  const year = company?.currentYear || new Date().getFullYear();
  const prefix = company?.invoicePrefix || 'FAC';
  const seq = String(invoiceData.number || 0).padStart(4, '0');
  return `${prefix}-${year}-${seq}`;
};

export const buildInvoiceApiPayload = (
  invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'> & {
    items?: any[];
    services?: any[];
    reimbursableExpenses?: any[];
    suplidos?: any[];
  },
  formattedNumber: string
) => {
  const services = invoiceData.services || invoiceData.items || [];
  const reimbursableExpenses = (invoiceData as any).reimbursableExpenses || [];
  const suplidos = (invoiceData as any).suplidos || [];
  const totals = calculateIssuedInvoiceTotals({
    services,
    reimbursableExpenses,
    suplidos,
    ivaRate: invoiceData.ivaPercentage,
    irpfRate: invoiceData.irpfPercentage
  });
  const legacyItems = [
    ...services.map((s: any) => ({
      description: s.description,
      quantity: s.quantity || 1,
      unitPrice: s.unitPrice ?? s.importe,
      subtotal: s.subtotal ?? s.importe,
      lineType: 'service'
    })),
    ...reimbursableExpenses.map((r: any) => ({
      description: r.description,
      quantity: r.quantity || 1,
      unitPrice: r.unitPrice ?? r.importe ?? r.ticket_base_amount,
      subtotal: r.subtotal ?? r.importe ?? r.ticket_base_amount,
      lineType: 'reimbursable_expense',
      tipo: r.tipo,
      expense_type: r.expense_type || r.expenseType || 'no_suplido',
      ticket_total: r.ticket_total ?? r.ticketTotal,
      ticket_base_amount: r.ticket_base_amount ?? r.ticketBaseAmount,
      ticket_vat_amount: r.ticket_vat_amount ?? r.ticketVatAmount,
      ticket_vat_rate: r.ticket_vat_rate ?? r.ticketVatRate
    }))
  ];
  return {
    number: formattedNumber,
    date: (invoiceData.date instanceof Date ? invoiceData.date : new Date(invoiceData.date))
      .toISOString()
      .split('T')[0],
    dueDate: invoiceData.dueDate
      ? (invoiceData.dueDate instanceof Date ? invoiceData.dueDate : new Date(invoiceData.dueDate))
          .toISOString()
          .split('T')[0]
      : null,
    clientId: invoiceData.clientId,
    projectId: (invoiceData as { projectId?: string | null }).projectId || null,
    groupIds: (invoiceData as { groupIds?: string[] }).groupIds || [],
    subtotal: totals.baseImponibleTotal,
    suplidosAmount: totals.suplidosAmount,
    ivaAmount: totals.ivaAmount,
    irpfAmount: totals.irpfAmount,
    total: totals.total,
    notes: invoiceData.notes || '',
    services,
    reimbursableExpenses,
    suplidos,
    items: legacyItems,
    paymentMethod: (invoiceData as any).paymentMethod || 'transfer',
    paymentTermsDays: (invoiceData as any).paymentTermsDays ?? 30,
    bankIban: (invoiceData as any).bankIban || null,
    bankHolder: (invoiceData as any).bankHolder || null,
    bankName: (invoiceData as any).bankName || null,
    paymentNotes: (invoiceData as any).paymentNotes || null,
    needsReview: !!(invoiceData as any).needsReview
  };
};

export const mapApiInvoiceToFrontend = (row: any, clients: { id: string }[]): Invoice => {
  const clientFromJoin = row.client || {
    id: row.client_id,
    name: row.client_name || 'Cliente',
    cif: row.client_cif || '',
    address: '',
    city: '',
    companyId: row.company_id,
    createdAt: new Date(),
    updatedAt: new Date()
  };
  const knownClient = clients.find((c) => c.id === row.client_id);
  const numberRaw = row.number;
  const parsedNumber =
    typeof numberRaw === 'string'
      ? parseInt(String(numberRaw).split('-').pop() || '0', 10)
      : Number(numberRaw || 0);
  return {
    id: row.id,
    number: parsedNumber,
    displayNumber: typeof numberRaw === 'string' ? numberRaw : String(numberRaw || ''),
    date: new Date(row.date),
    clientId: row.client_id,
    client: knownClient ? ({ ...knownClient, ...clientFromJoin } as any) : (clientFromJoin as any),
    companyId: row.company_id,
    projectId: row.project_id || row.projectId || null,
    groupIds: (row.groups || []).map((g: { id: string }) => g.id),
    groups: row.groups || [],
    description: row.description || '',
    unitPrice: Number(row.unit_price || 0),
    quantity: Number(row.quantity || 1),
    subtotal: Number(row.subtotal || row.base_amount || 0),
    suplidosAmount: Number(row.suplidos_amount ?? 0),
    ivaPercentage: Number(row.iva_percentage ?? 21),
    ivaAmount: Number(row.iva_amount ?? 0),
    irpfPercentage: Number(row.irpf_percentage ?? 15),
    irpfAmount: Number(row.irpf_amount ?? 0),
    total: Number(row.total || 0),
    status: row.status,
    dueDate: row.due_date ? new Date(row.due_date) : undefined,
    notes: row.notes || undefined,
    paymentMethod: row.payment_method || 'transfer',
    paymentTermsDays: Number(row.payment_terms_days ?? 30),
    bankIban: row.bank_iban || undefined,
    bankHolder: row.bank_holder || undefined,
    bankName: row.bank_name || undefined,
    paymentNotes: row.payment_notes || undefined,
    services: row.services,
    reimbursableExpenses: row.reimbursableExpenses,
    suplidos: row.suplidos,
    fiscalBreakdown: row.fiscalBreakdown ?? row.fiscal_breakdown,
    needsReview: !!(row.needs_review ?? row.needsReview),
    createdAt: new Date(row.created_at || Date.now()),
    updatedAt: new Date(row.updated_at || Date.now())
  } as Invoice;
};
